import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { delay, filter, take, takeUntil } from 'rxjs/operators';

import { COWTUXConfigService } from '@cowtux/services/config.service';
import { COWTUXNavigationService } from '@cowtux/components/navigation/navigation.service';
import { COWTUXPerfectScrollbarDirective } from '@cowtux/directives/cowtux-perfect-scrollbar/cowtux-perfect-scrollbar.directive';
import { COWTUXSidebarService } from '@cowtux/components/sidebar/sidebar.service';

@Component({
    selector     : 'navbar-vertical-style-1',
    templateUrl  : './style-1.component.html',
    styleUrls    : ['./style-1.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NavbarVerticalStyle1Component implements OnInit, OnDestroy
{
    COWTUXConfig: any;
    navigation: any;

    // Private
    private _cowtuxPerfectScrollbar: COWTUXPerfectScrollbarDirective;
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {COWTUXConfigService} _cowtuxConfigService
     * @param {COWTUXNavigationService} _cowtuxNavigationService
     * @param {COWTUXSidebarService} _cowtuxSidebarService
     * @param {Router} _router
     */
    constructor(
        private _cowtuxConfigService: COWTUXConfigService,
        private _cowtuxNavigationService: COWTUXNavigationService,
        private _cowtuxSidebarService: COWTUXSidebarService,
        private _router: Router
    )
    {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    // Directive
    @ViewChild(COWTUXPerfectScrollbarDirective, {static: true})
    set directive(theDirective: COWTUXPerfectScrollbarDirective)
    {
        if ( !theDirective )
        {
            return;
        }

        this._cowtuxPerfectScrollbar = theDirective;

        // Update the scrollbar on collapsable item toggle
        this._cowtuxNavigationService.onItemCollapseToggled
            .pipe(
                delay(500),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._cowtuxPerfectScrollbar.update();
            });

        // Scroll to the active item position
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => {
                    setTimeout(() => {
                        this._cowtuxPerfectScrollbar.scrollToElement('navbar .nav-link.active', -120);
                    });
                }
            );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                    if ( this._cowtuxSidebarService.getSidebar('navbar') )
                    {
                        this._cowtuxSidebarService.getSidebar('navbar').close();
                    }
                }
            );

        // Subscribe to the config changes
        this._cowtuxConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                this.COWTUXConfig = config;
            });

        // Get current navigation
        this._cowtuxNavigationService.onNavigationChanged
            .pipe(
                filter(value => value !== null),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.navigation = this._cowtuxNavigationService.getCurrentNavigation();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar opened status
     */
    toggleSidebarOpened(): void
    {
        this._cowtuxSidebarService.getSidebar('navbar').toggleOpen();
    }

    /**
     * Toggle sidebar folded status
     */
    toggleSidebarFolded(): void
    {
        this._cowtuxSidebarService.getSidebar('navbar').toggleFold();
    }
}
